import { normalizeAbsoluteDateValue } from "./dateRangeLimits.ts";
import {
	annualRevenueBounds,
	annualRevenueLadder,
	annualRevenuePresets,
	formatNumericRangeCompact,
	normalizeNumericRange,
	numberEmployeesBounds,
	numberEmployeesLadder,
	numberEmployeesPresets,
	type NumericRange,
	type NumericRangeBounds,
	type NumericRangeKind,
	type NumericRangePreset,
} from "./uccNumericRange.ts";

export type UccSearchBy = "debtor" | "secured_party" | "company";

export const uccSearchByOptions = [
	{ value: "debtor", label: "Debtor" },
	{ value: "secured_party", label: "Secured Party" },
	// Temporarily disabled until UCC company search is ready for release.
	// { value: "company", label: "Company" },
] as const;

// Uncomment the option above to restore the selector, links and search requests.
export const UCC_COMPANY_SEARCH_ENABLED = uccSearchByOptions.some(
	(option: { value: UccSearchBy }) => option.value === "company"
);

// Phone accordion groups, in reading order. "identity" has no panel fields: as
// with debtor and secured party names, those values come from the header search.
export const uccCompanyFilterGroups = [
	{ key: "location", label: "Company" },
	{ key: "industry", label: "Industry" },
	{ key: "size", label: "Company size" },
	{ key: "activity", label: "UCC activity" },
	{ key: "financing", label: "Lenders" },
	{ key: "collateral", label: "Collateral" },
	{ key: "dates", label: "Dates" },
	{ key: "options", label: "Options" },
] as const;
export type UccCompanyFilterGroup = (typeof uccCompanyFilterGroups)[number]["key"] | "identity";

// These are indexed fields in ucc_companies, not aliases for ucc_records.
export const uccCompanyTextFilters = [
	{ key: "company_name", label: "Company Name", group: "identity" },
	{ key: "base_url", label: "Company Website", group: "identity" },
	{ key: "phone", label: "Company Phone", group: "identity" },
	{ key: "duns_number", label: "DUNS Number", group: "identity" },
	{ key: "industry", label: "Company Industry", group: "industry" },
	{ key: "naics_code", label: "NAICS Code", group: "industry" },
	{ key: "two_digit_sic_code", label: "2-Digit SIC Code", group: "industry" },
	{ key: "four_digit_sic_code", label: "4-Digit SIC Code", group: "industry" },
	{ key: "eight_digit_sic_code", label: "8-Digit SIC Code", group: "industry" },
	{ key: "top_lenders", label: "Lenders", group: "financing" },
	// Searched in the collateral text of the company's filings, not on the company record.
	{ key: "collateral", label: "Collateral Contains", group: "collateral" },
	{ key: "asset_category", label: "Collateral Category", group: "collateral" },
	{ key: "asset_equipment_type", label: "Equipment Type", group: "collateral" },
	{ key: "asset_equipment_subtype", label: "Equipment Subtype", group: "collateral" },
	{ key: "top_brands", label: "Equipment Brand", group: "collateral" },
] as const;
export type UccCompanyTextField = (typeof uccCompanyTextFilters)[number]["key"];

// Lists typed into the filter panel. Lenders must match exactly; collateral entries are
// phrases found anywhere in a filing's collateral, as in the filing search.
export const UCC_COMPANY_COLLATERAL_INPUT_LIMIT = 10;
export const uccCompanyTypedFilters = [
	{ key: "top_lenders", label: "Lenders", placeholder: "Enter lender name", group: "financing", max: 50 },
	{ key: "collateral", label: "Collateral Contains", placeholder: `Enter a phrase and press Enter (up to ${UCC_COMPANY_COLLATERAL_INPUT_LIMIT})`, group: "collateral", max: UCC_COMPANY_COLLATERAL_INPUT_LIMIT },
] as const satisfies readonly {
	key: UccCompanyTextField;
	label: string;
	placeholder: string;
	group: UccCompanyFilterGroup;
	max: number;
}[];

// Collateral Type is filtered on what the company's filings secure, rolled up onto the
// company record. Each level maps a filing collateral picklist onto its company field.
export const uccCompanyCollateralLevels = [
	{ assetField: "asset_category", key: "asset_category" },
	{ assetField: "asset_equipment_type", key: "asset_equipment_type" },
	{ assetField: "asset_equipment_subtype", key: "asset_equipment_subtype" },
	{ assetField: "asset_brand", key: "top_brands" },
] as const satisfies readonly { assetField: string; key: UccCompanyTextField }[];

const filingCountBounds: NumericRangeBounds = { min: 0, max: 1_000, step: 1 };
const filingCountLadder: readonly number[] = [0, 1, 2, 3, 5, 10, 15, 20, 25, 50, 75, 100, 250, 500, 1_000];
const filingCountPresets: readonly NumericRangePreset[] = [
	{ label: "1 - 5", min: 1, max: 5 },
	{ label: "6 - 10", min: 6, max: 10 },
	{ label: "11 - 50", min: 11, max: 50 },
	{ label: "51+", min: 51, max: filingCountBounds.max },
];
const lenderCountBounds: NumericRangeBounds = { min: 0, max: 100, step: 1 };
const lenderCountLadder: readonly number[] = [0, 1, 2, 3, 4, 5, 10, 15, 20, 25, 50, 100];
const lenderCountPresets: readonly NumericRangePreset[] = [
	{ label: "1 lender", min: 1, max: 1 },
	{ label: "2 - 3", min: 2, max: 3 },
	{ label: "4 - 5", min: 4, max: 5 },
	{ label: "6+", min: 6, max: lenderCountBounds.max },
];

// Every range is open at the top: a maximum left at its bound means "and above".
export const uccCompanyNumberFilters = [
	{ key: "annual_revenue", label: "Annual Revenue", rangeLabel: "annual revenue", group: "size", kind: "currency", bounds: annualRevenueBounds, ladder: annualRevenueLadder, presets: annualRevenuePresets },
	{ key: "estimated_number_employees", label: "Number of Employees", rangeLabel: "employee count", group: "size", kind: "count", bounds: numberEmployeesBounds, ladder: numberEmployeesLadder, presets: numberEmployeesPresets },
	{ key: "total_number_of_filings", label: "Total Filings", rangeLabel: "total filings", group: "activity", kind: "count", bounds: filingCountBounds, ladder: filingCountLadder, presets: filingCountPresets },
	{ key: "active_filings_count", label: "Active Filings", rangeLabel: "active filings", group: "activity", kind: "count", bounds: filingCountBounds, ladder: filingCountLadder, presets: filingCountPresets },
	{ key: "filings_last_12_months", label: "Filings in Last 12 Months", rangeLabel: "filings in the last 12 months", group: "activity", kind: "count", bounds: filingCountBounds, ladder: filingCountLadder, presets: filingCountPresets },
	{ key: "number_of_unique_lenders", label: "Number of Lenders", rangeLabel: "lender count", group: "activity", kind: "count", bounds: lenderCountBounds, ladder: lenderCountLadder, presets: lenderCountPresets },
] as const satisfies readonly {
	key: string;
	label: string;
	rangeLabel: string;
	group: UccCompanyFilterGroup;
	kind: NumericRangeKind;
	bounds: NumericRangeBounds;
	ladder: readonly number[];
	presets: readonly NumericRangePreset[];
}[];
export type UccCompanyNumberFilter = (typeof uccCompanyNumberFilters)[number];
export type UccCompanyNumberField = UccCompanyNumberFilter["key"];

export const uccCompanyDateFilters = [
	{ key: "first_filing_date", label: "First Filing Date", rangeLabel: "First filing date range", past: true },
	{ key: "last_lien_date", label: "Latest Lien Date", rangeLabel: "Latest lien date range", past: true },
	{ key: "next_lapse_date", label: "Next Lapse Date", rangeLabel: "Next lapse date range", past: false },
] as const;
export type UccCompanyDateField = (typeof uccCompanyDateFilters)[number]["key"];

export const uccCompanyBooleanFilters = [
	{ key: "has_contacts", label: "Has Contacts" },
	{ key: "has_blanket_lien", label: "Has Blanket Lien" },
	{ key: "has_mca_filing", label: "Has MCA Filing" },
	{ key: "has_captive_financing", label: "Has Captive Financing" },
	{ key: "has_ar_factoring_lien", label: "Has A/R Factoring Lien" },
	{ key: "lien_stacking_flag", label: "Has Stacked Liens" },
	{ key: "continuation_window_open", label: "Continuation Window Open" },
] as const;
export type UccCompanyBooleanField = (typeof uccCompanyBooleanFilters)[number]["key"];

export const uccCompanySortOptions = [
	{ value: "last_lien_date_unix", label: "Latest Lien Date", order: "date" },
	{ value: "next_lapse_date_unix", label: "Next Lapse Date", order: "date" },
	{ value: "total_number_of_filings", label: "Total Filings", order: "number" },
	{ value: "annual_revenue", label: "Annual Revenue", order: "number" },
	{ value: "estimated_number_employees", label: "Number of Employees", order: "number" },
] as const;
export type UccCompanySortField = (typeof uccCompanySortOptions)[number]["value"];

export function uccCompanySortOrderLabel(sortBy: UccCompanySortField, sortOrder: "asc" | "desc") {
	const numeric = uccCompanySortOptions.find((option) => option.value === sortBy)?.order === "number";
	if (numeric) return sortOrder === "desc" ? "Highest" : "Lowest";
	return sortOrder === "desc" ? "Latest" : "Earliest";
}

export const uccCompanyHeaderOptions = [
	{ id: "name", value: "name", label: "Company Name", selected: false },
	{ id: "url", value: "url", label: "Company Website", selected: false },
	{ id: "phone", value: "phone", label: "Company Phone", selected: false },
	{ id: "duns_number", value: "duns_number", label: "DUNS Number", selected: false },
] as const;

const headerFields = { name: "company_name", url: "base_url", phone: "phone", duns_number: "duns_number" } as const;
export const uccCompanyIdentityFields = Object.values(headerFields);

export function uccCompanyHeaderField(field: string): UccCompanyTextField | null {
	return headerFields[field as keyof typeof headerFields] ?? null;
}

export type UccCompanyFlagValue = "" | "true" | "false";
export type UccCompanyFilters = {
	text: Record<UccCompanyTextField, string[]>;
	numbers: Record<UccCompanyNumberField, NumericRange>;
	dates: Record<UccCompanyDateField, [string, string]>;
	// The panel only offers "Yes"; "false" is kept so an explicit "No" can be sent.
	flags: Record<UccCompanyBooleanField, UccCompanyFlagValue>;
	state: string[];
	// How several Collateral Contains entries combine within one filing.
	collateral_operator: "AND" | "OR";
	sort_by: UccCompanySortField;
	sort_order: "asc" | "desc";
};

export type UccCompanySearchPayload = Partial<Record<UccCompanyTextField | "state", string[]>> &
	Partial<Record<UccCompanyNumberField, [number | null, number | null]>> &
	Partial<Record<UccCompanyDateField, [string | null, string | null]>> &
	Partial<Record<UccCompanyBooleanField, boolean>> & {
		collateral_contains_operator?: "AND" | "OR";
		page: number;
		per_page: number;
		sort_by: UccCompanySortField;
		sort_order: "asc" | "desc";
	};

export type UccCompanyFilterSummary = { label: string; value: string; group: UccCompanyFilterGroup };

export const UCC_COMPANY_SEARCH_PER_PAGE = 25;

export function createUccCompanyFilters(): UccCompanyFilters {
	return {
		text: Object.fromEntries(uccCompanyTextFilters.map(({ key }) => [key, []])) as unknown as UccCompanyFilters["text"],
		numbers: Object.fromEntries(uccCompanyNumberFilters.map(({ key, bounds }) => [key, [bounds.min, bounds.max]])) as UccCompanyFilters["numbers"],
		dates: Object.fromEntries(uccCompanyDateFilters.map(({ key }) => [key, ["", ""]])) as UccCompanyFilters["dates"],
		flags: Object.fromEntries(uccCompanyBooleanFilters.map(({ key }) => [key, ""])) as UccCompanyFilters["flags"],
		state: [],
		collateral_operator: "AND",
		sort_by: "last_lien_date_unix",
		sort_order: "desc",
	};
}

export function uccCompanySearchValues(value: unknown): string[] {
	let values: unknown[] = Array.isArray(value) ? value : [value];
	if (typeof value === "string" && value.trim().startsWith("[")) {
		try {
			const parsed = JSON.parse(value);
			if (Array.isArray(parsed)) values = parsed;
		} catch {
			/* Treat ordinary text as one input. */
		}
	}
	return [
		...new Set(
			values
				.flatMap((item) => String(item ?? "").split(/[;\n]+/))
				.map((item) => item.trim())
				.filter(Boolean)
		),
	];
}

function numberRangeBounds({ bounds }: UccCompanyNumberFilter, value: NumericRange): [number | null, number | null] {
	const [min, max] = normalizeNumericRange(value, bounds);
	return [min > bounds.min ? min : null, max < bounds.max ? max : null];
}

export function formatUccCompanyNumberRange(filter: UccCompanyNumberFilter, value: NumericRange) {
	const [min, max] = normalizeNumericRange(value, filter.bounds);
	return `${formatNumericRangeCompact(min, filter.kind)} - ${formatNumericRangeCompact(max, filter.kind)}${max === filter.bounds.max ? "+" : ""}`;
}

function dateRangeBounds(label: string, [start, end]: [string, string]): [string | null, string | null] {
	const [from, through] = [start, end].map((value) => {
		if (!value?.trim()) return null;
		const normalized = normalizeAbsoluteDateValue(value);
		if (!normalized) throw new Error(`${label}: enter dates as MM/DD/YYYY.`);
		return normalized;
	});
	// A reversed range is read the way the filing date filters read it.
	return from && through && from > through ? [through, from] : [from, through];
}

export function buildUccCompanySearchPayload(filters: UccCompanyFilters, page = 1): UccCompanySearchPayload {
	const payload: UccCompanySearchPayload = { page, per_page: UCC_COMPANY_SEARCH_PER_PAGE, sort_by: filters.sort_by, sort_order: filters.sort_order };
	for (const { key } of uccCompanyTextFilters) {
		const values = uccCompanySearchValues(filters.text[key]);
		if (values.length) payload[key] = values;
	}
	if ((payload.collateral?.length ?? 0) > 1) payload.collateral_contains_operator = filters.collateral_operator;
	if (filters.state.length) payload.state = [...filters.state];
	for (const filter of uccCompanyNumberFilters) {
		const [min, max] = numberRangeBounds(filter, filters.numbers[filter.key]);
		if (min != null || max != null) payload[filter.key] = [min, max];
	}
	for (const { key, label } of uccCompanyDateFilters) {
		const [start, end] = dateRangeBounds(label, filters.dates[key]);
		if (start || end) payload[key] = [start, end];
	}
	for (const { key } of uccCompanyBooleanFilters) {
		if (filters.flags[key]) payload[key] = filters.flags[key] === "true";
	}
	return payload;
}

/**
 * Rebuilds the panel filters from a submitted payload (an export's stored search).
 * Unknown keys are ignored. `header` is the identity field the header search owns.
 */
export function uccCompanyFiltersFromPayload(payload: Record<string, unknown>): { filters: UccCompanyFilters; header: { field: string; values: string[] } | null } {
	const filters = createUccCompanyFilters();
	const list = (value: unknown) => (Array.isArray(value) ? uccCompanySearchValues(value) : []);
	const identity = new Set<string>(uccCompanyIdentityFields);
	for (const { key } of uccCompanyTextFilters) {
		if (!identity.has(key)) filters.text[key] = list(payload[key]);
	}
	if (payload.collateral_contains_operator === "OR") filters.collateral_operator = "OR";
	filters.state = list(payload.state);
	for (const { key, bounds } of uccCompanyNumberFilters) {
		const value = payload[key];
		if (!Array.isArray(value)) continue;
		const bound = (item: unknown, fallback: number) => (typeof item === "number" && Number.isFinite(item) ? item : fallback);
		filters.numbers[key] = normalizeNumericRange([bound(value[0], bounds.min), bound(value[1], bounds.max)], bounds);
	}
	for (const { key } of uccCompanyDateFilters) {
		const value = payload[key];
		if (Array.isArray(value)) filters.dates[key] = [typeof value[0] === "string" ? value[0] : "", typeof value[1] === "string" ? value[1] : ""];
	}
	for (const { key } of uccCompanyBooleanFilters) {
		if (typeof payload[key] === "boolean") filters.flags[key] = payload[key] ? "true" : "false";
	}
	const sort = uccCompanySortOptions.find((option) => option.value === payload.sort_by);
	if (sort) filters.sort_by = sort.value;
	if (payload.sort_order === "asc" || payload.sort_order === "desc") filters.sort_order = payload.sort_order;
	const headerField = Object.entries(headerFields).find(([, key]) => list(payload[key]).length > 0);
	return { filters, header: headerField ? { field: headerField[0], values: list(payload[headerField[1]]) } : null };
}

/** One entry per active filter, for the "N selected" count and its tooltip. */
export function uccCompanyFilterSummaries(filters: UccCompanyFilters): UccCompanyFilterSummary[] {
	const summaries: UccCompanyFilterSummary[] = [];
	for (const { key, label, group } of uccCompanyTextFilters) {
		const values = uccCompanySearchValues(filters.text[key]);
		if (values.length) summaries.push({ label, value: values.join(key === "collateral" ? ` ${filters.collateral_operator} ` : ", "), group });
	}
	if (filters.state.length) summaries.push({ label: "Company State", value: filters.state.join(", "), group: "location" });
	for (const filter of uccCompanyNumberFilters) {
		const [min, max] = numberRangeBounds(filter, filters.numbers[filter.key]);
		if (min != null || max != null) summaries.push({ label: filter.label, value: formatUccCompanyNumberRange(filter, filters.numbers[filter.key]), group: filter.group });
	}
	for (const { key, label } of uccCompanyDateFilters) {
		const [start, end] = filters.dates[key].map((value) => value?.trim() ?? "");
		if (start || end) summaries.push({ label, value: start && end ? `${start} - ${end}` : start ? `From ${start}` : `Through ${end}`, group: "dates" });
	}
	for (const { key, label } of uccCompanyBooleanFilters) {
		if (filters.flags[key]) summaries.push({ label, value: filters.flags[key] === "true" ? "Yes" : "No", group: "options" });
	}
	return summaries;
}

export function uccCompanyFilterCount(filters: UccCompanyFilters): number {
	return uccCompanyFilterSummaries(filters).length;
}
